import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, RefreshCcw } from "lucide-react";
import { PageHeader, Pill, SectionCard } from "../../components/Ui";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { api, extractError } from "../../lib/api";
import { formatListDate } from "../../lib/purchaseListMeta";
import { toast } from "sonner";
import {
  DonutMeter,
  MajorKpiGrid,
  formatCurrency,
  formatPartyKind,
  formatPct,
  formatQty,
} from "../purchase/purchaseReportShared";
import PartyTdsAnalysisPanel from "../../components/PartyTdsAnalysisPanel";

const DEFAULT_FILTERS = {
  from_date: "",
  to_date: "",
  variety: "",
};

export default function SalesPartyProfilePage() {
  const { id } = useParams();
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [tab, setTab] = useState("ledger");
  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState(null);

  const params = useMemo(
    () => ({
      from_date: filters.from_date || undefined,
      to_date: filters.to_date || undefined,
      variety: filters.variety || undefined,
    }),
    [filters],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/sales-reports/party/${id}`, { params });
      setPayload(data);
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setLoading(false);
    }
  }, [id, params]);

  useEffect(() => {
    load();
  }, [load]);

  const contracted = Number(payload?.kpis?.contracted_qty || 0);
  const dispatched = Number(payload?.kpis?.dispatched_qtl || 0);
  const fulfillmentPct = contracted > 0 ? (dispatched / contracted) * 100 : 0;
  const isBroker = Boolean(payload?.party?.is_broker);

  return (
    <div className="space-y-6 sm:space-y-8" data-testid="sales-party-profile-page">
      <PageHeader
        eyebrow="Sales Reports"
        title={payload?.party?.name || "Party / Broker Ledger"}
        description={
          isBroker
            ? "Broker profile with linked sales, brokerage due, and order fulfillment."
            : "Party ledger with pending/completed sales orders, full-rate sales, deductions, and due payments."
        }
        actions={
          <>
            <Button type="button" variant="outline" asChild>
              <Link to="/sales/reports">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to dashboard
              </Link>
            </Button>
            <Button type="button" variant="outline" onClick={load} disabled={loading}>
              <RefreshCcw className="h-4 w-4 mr-2" />
              {loading ? "Refreshing..." : "Refresh"}
            </Button>
          </>
        }
      />

      <SectionCard title="Profile Scope">
        <div className="flex flex-wrap items-center gap-2">
          <Pill tone={isBroker ? "gold" : "sage"}>{formatPartyKind(payload?.party?.kind)}</Pill>
          <span className="text-sm text-muted-foreground">
            Sales amounts use the complete contract rate; weight/price cuts, CD, and brokerage are applied afterwards.
          </span>
        </div>
      </SectionCard>

      <SectionCard title="Filters">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="From date">
            <Input type="date" value={filters.from_date} onChange={(e) => setFilters((f) => ({ ...f, from_date: e.target.value }))} />
          </Field>
          <Field label="To date">
            <Input type="date" value={filters.to_date} onChange={(e) => setFilters((f) => ({ ...f, to_date: e.target.value }))} />
          </Field>
          <Field label="Variety">
            <Input placeholder="Variety..." value={filters.variety} onChange={(e) => setFilters((f) => ({ ...f, variety: e.target.value }))} />
          </Field>
        </div>
        <div className="mt-4">
          <Button type="button" variant="outline" onClick={() => setFilters(DEFAULT_FILTERS)}>Clear filters</Button>
        </div>
      </SectionCard>

      <div className="grid grid-cols-1 xl:grid-cols-[1.1fr_2fr] gap-4">
        <SectionCard title="Order Fulfillment">
          <DonutMeter
            value={fulfillmentPct}
            center={`${formatQty(dispatched)} qtl`}
            label="Dispatched"
            sublabel={`${formatQty(payload?.kpis?.pending_order_qtl)} qtl pending against ${formatQty(contracted)} qtl ordered`}
          />
        </SectionCard>
        <MajorKpiGrid
          items={[
            { label: "Contracted qty", value: `${formatQty(payload?.kpis?.contracted_qty)} qtl`, accent: true },
            { label: "Dispatched qty", value: `${formatQty(payload?.kpis?.dispatched_qtl)} qtl` },
            { label: "Pending order qty", value: `${formatQty(payload?.kpis?.pending_order_qtl)} qtl` },
            { label: "Full sales amount", value: formatCurrency(payload?.kpis?.full_sales_amount), sub: "Complete rate" },
            { label: isBroker ? "Brokerage due" : "Due payments", value: formatCurrency(isBroker ? payload?.kpis?.brokerage_due : payload?.kpis?.due_payments), accent: true },
            { label: "Pending / completed", value: `${payload?.kpis?.pending_orders ?? 0} / ${payload?.kpis?.completed_orders ?? 0}` },
          ]}
        />
      </div>

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList className="bg-card border border-border p-1 h-auto flex-wrap">
          <TabsTrigger value="ledger">Ledger</TabsTrigger>
          <TabsTrigger value="contracts">Contracts</TabsTrigger>
          <TabsTrigger value="vouchers">Vouchers</TabsTrigger>
          <TabsTrigger value="pending">Pending Orders</TabsTrigger>
          <TabsTrigger value="completed">Completed Orders</TabsTrigger>
          <TabsTrigger value="tds">TDS analysis</TabsTrigger>
        </TabsList>

        <TabsContent value="ledger">
          <SectionCard title="Sales Ledger" subtitle="Full amount, deductions afterwards, receipts, and running dues.">
            <SimpleTable
              columns={["Date", "Type", "Ref", "Debit", "Credit", "Brokerage", "Due", "Note"]}
              rows={(payload?.ledger_entries || []).map((row, idx) => [
                formatListDate(row.at),
                row.type,
                row.ref || "—",
                formatCurrency(row.debit),
                formatCurrency(row.credit),
                formatCurrency(row.brokerage),
                formatCurrency(row.balance_due),
                <div key={`note-${idx}`} className="min-w-[220px] whitespace-normal">
                  <div>{row.note || "—"}</div>
                  {row.deductions ? (
                    <div className="text-xs text-muted-foreground mt-1">
                      Cuts: {formatCurrency((row.deductions.weight_cut || 0) + (row.deductions.price_cut || 0))}
                      {" · CD: "}
                      {formatCurrency(row.deductions.cd)}
                      {" · Brk: "}
                      {formatCurrency(row.deductions.brokerage)}
                    </div>
                  ) : null}
                </div>,
              ])}
              emptyMessage={loading ? "Loading ledger..." : "No ledger entries for this party."}
            />
          </SectionCard>
        </TabsContent>

        <TabsContent value="contracts">
          <SectionCard title="Contracts Ledger">
            <SimpleTable
              columns={["Contract", "Date", "Variety", "Rate", "Ordered", "Dispatched", "Pending", "Progress", "Status"]}
              rows={(payload?.contracts_ledger || []).map((row) => [
                row.contract_number,
                formatListDate(row.contract_date),
                `${row.variety || "—"}${row.grade ? ` · ${row.grade}` : ""}`,
                formatCurrency(row.agreed_rate),
                `${formatQty(row.quantity_qtl)} qtl`,
                `${formatQty(row.dispatched_qtl)} qtl`,
                `${formatQty(row.pending_qtl)} qtl`,
                formatPct(row.progress_pct),
                <Pill key={`st-${row.id}`} tone={row.order_status === "COMPLETED" ? "sage" : "gold"}>
                  {row.order_status || row.status}
                </Pill>,
              ])}
              emptyMessage={loading ? "Loading contracts..." : "No contracts found for this party."}
            />
          </SectionCard>
        </TabsContent>

        <TabsContent value="vouchers">
          <SectionCard title="Sales Vouchers" subtitle="Full rate sales with deductions applied afterwards.">
            <SimpleTable
              columns={["Date", "Voucher", "Variety", "Qty", "Full amount", "Deductions", "Receivable", "Due", "Brokerage"]}
              rows={(payload?.voucher_log || []).map((row) => [
                formatListDate(row.created_at),
                row.code,
                row.variety || "—",
                `${formatQty(row.billable_qtl || row.net_qtl)} qtl`,
                formatCurrency(row.full_amount),
                formatCurrency(
                  (row.weight_cut_deduction || 0) + (row.price_cut_deduction || 0) + (row.cd_amount || 0),
                ),
                formatCurrency(row.buyer_receivable),
                formatCurrency(row.balance_due),
                formatCurrency(row.brokerage_amount),
              ])}
              emptyMessage={loading ? "Loading vouchers..." : "No vouchers for this party."}
            />
          </SectionCard>
        </TabsContent>

        <TabsContent value="pending">
          <SectionCard title="Pending Sales Orders">
            <OrderList rows={payload?.pending_orders || []} loading={loading} empty="No pending orders." />
          </SectionCard>
        </TabsContent>

        <TabsContent value="completed">
          <SectionCard title="Completed Sales Orders">
            <OrderList rows={payload?.completed_orders || []} loading={loading} empty="No completed orders." />
          </SectionCard>
        </TabsContent>

        <TabsContent value="tds">
          <PartyTdsAnalysisPanel partyId={id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function OrderList({ rows, loading, empty }) {
  if (!rows.length) {
    return <EmptyText text={loading ? "Loading..." : empty} />;
  }
  return (
    <div className="space-y-3">
      {rows.map((row) => (
        <div key={row.id} className="rounded-xl border border-border/70 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <div className="font-semibold">{row.contract_number}</div>
            <div className="text-sm text-muted-foreground mt-1">
              {row.variety || "—"} · Payment: {row.payment_condition || "—"} · Rate {formatCurrency(row.agreed_rate)}
            </div>
          </div>
          <div className="text-sm">
            {formatQty(row.dispatched_qtl)} / {formatQty(row.quantity_qtl)} qtl · Pending {formatQty(row.pending_qtl)} qtl
          </div>
        </div>
      ))}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

function EmptyText({ text }) {
  return <div className="py-12 text-center text-sm text-muted-foreground">{text}</div>;
}

function SimpleTable({ columns, rows, emptyMessage }) {
  if (!rows.length) {
    return <EmptyText text={emptyMessage} />;
  }
  return (
    <div className="overflow-x-auto rounded-lg border border-border/70">
      <table className="w-full text-sm min-w-[920px]">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-[0.08em] font-medium text-muted-foreground bg-muted border-b border-border">
            {columns.map((column) => (
              <th key={column} className="px-3 py-2">{column}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={index} className="border-b border-border/60 align-top">
              {row.map((cell, cellIndex) => (
                <td key={cellIndex} className="px-3 py-2 whitespace-nowrap">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
